import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useThemeClasses } from "@/hooks/useThemeClasses";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShoppingCart, Search, User, Package, Plus, X, Minus, CreditCard, CheckCircle, Shield, Percent, Headphones } from "lucide-react";
import { toast } from "sonner";

const PontoVenda = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user } = useAuth();
  const lojaSlug = slug || user?.loja_slug;
  const { isDark, card, input, textPrimary, textSecondary, textMuted, cardHeader } = useThemeClasses();
  
  const [produtos, setProdutos] = useState([]);
  const [acessorios, setAcessorios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchProduto, setSearchProduto] = useState("");
  const [searchAcessorio, setSearchAcessorio] = useState("");
  const [searchCliente, setSearchCliente] = useState("");
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedProdutos, setSelectedProdutos] = useState([]);
  const [selectedAcessorios, setSelectedAcessorios] = useState([]); // [{id, nome, preco, quantidade}]
  const [formaPagamento, setFormaPagamento] = useState("");
  const [observacao, setObservacao] = useState("");
  const [garantiaMeses, setGarantiaMeses] = useState("0");
  const [garantiaInicio, setGarantiaInicio] = useState(() => new Date().toISOString().split("T")[0]);
  const [desconto, setDesconto] = useState("");
  const [possuiTroca, setPossuiTroca] = useState(false);
  const [trocaModeloId, setTrocaModeloId] = useState("");
  const [trocaCor, setTrocaCor] = useState("");
  const [trocaMemoria, setTrocaMemoria] = useState("");
  const [trocaMemoriaRam, setTrocaMemoriaRam] = useState("");
  const [trocaBateria, setTrocaBateria] = useState("");
  const [trocaImei, setTrocaImei] = useState("");
  const [trocaValorRecebido, setTrocaValorRecebido] = useState("");
  const [activeTab, setActiveTab] = useState("celulares");

  useEffect(() => { if (lojaSlug) fetchData(); }, [lojaSlug]);

  const fetchData = async () => {
    try {
      const [produtosRes, acessoriosRes, clientesRes, modelosRes] = await Promise.all([
        axios.get(`${API}/loja/${lojaSlug}/produtos`, { params: { vendido: false } }),
        axios.get(`${API}/loja/${lojaSlug}/acessorios`),
        axios.get(`${API}/loja/${lojaSlug}/clientes`),
        axios.get(`${API}/loja/${lojaSlug}/modelos`)
      ]);
      setProdutos(produtosRes.data);
      setAcessorios(acessoriosRes.data.filter(a => a.quantidade > 0));
      setClientes(clientesRes.data);
      setModelos(modelosRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  const filteredProdutos = useMemo(() => {
    return produtos.filter((p) => {
      const s = searchProduto.toLowerCase();
      const notSelected = !selectedProdutos.some(sp => sp.id === p.id);
      return notSelected && (p.modelo_nome?.toLowerCase().includes(s) || p.cor.toLowerCase().includes(s) || (p.memoria || "").toLowerCase().includes(s));
    });
  }, [produtos, searchProduto, selectedProdutos]);

  const filteredAcessorios = useMemo(() => {
    return acessorios.filter((a) => {
      const s = searchAcessorio.toLowerCase();
      return a.nome.toLowerCase().includes(s) || a.categoria.toLowerCase().includes(s);
    });
  }, [acessorios, searchAcessorio]);

  const filteredClientes = useMemo(() => {
    return clientes.filter((c) => {
      const s = searchCliente.toLowerCase();
      return c.nome.toLowerCase().includes(s) || c.cpf.includes(searchCliente) || c.whatsapp.includes(searchCliente);
    });
  }, [clientes, searchCliente]);

  const totalCelulares = useMemo(() => selectedProdutos.reduce((sum, p) => sum + p.preco, 0), [selectedProdutos]);
  const totalAcessorios = useMemo(() => selectedAcessorios.reduce((sum, a) => sum + (a.preco * a.quantidade), 0), [selectedAcessorios]);
  const total = totalCelulares + totalAcessorios;
  
  const descontoValue = useMemo(() => {
    if (possuiTroca) return parseFloat(trocaValorRecebido) || 0;
    return parseFloat(desconto) || 0;
  }, [desconto, possuiTroca, trocaValorRecebido]);
  const totalComDesconto = useMemo(() => Math.max(0, total - descontoValue), [total, descontoValue]);

  const addAcessorio = (acessorio) => {
    const existing = selectedAcessorios.find(a => a.id === acessorio.id);
    if (existing) {
      if (existing.quantidade < acessorio.quantidade) {
        setSelectedAcessorios(selectedAcessorios.map(a => 
          a.id === acessorio.id ? {...a, quantidade: a.quantidade + 1} : a
        ));
      } else {
        toast.error("Estoque insuficiente");
      }
    } else {
      setSelectedAcessorios([...selectedAcessorios, {
        id: acessorio.id,
        nome: acessorio.nome,
        preco: acessorio.preco,
        quantidade: 1,
        maxQuantidade: acessorio.quantidade
      }]);
    }
  };

  const updateAcessorioQtd = (id, delta) => {
    setSelectedAcessorios(selectedAcessorios.map(a => {
      if (a.id === id) {
        const newQtd = a.quantidade + delta;
        if (newQtd <= 0) return null;
        if (newQtd > a.maxQuantidade) {
          toast.error("Estoque insuficiente");
          return a;
        }
        return {...a, quantidade: newQtd};
      }
      return a;
    }).filter(Boolean));
  };

  const removeAcessorio = (id) => {
    setSelectedAcessorios(selectedAcessorios.filter(a => a.id !== id));
  };

  const handleSubmit = async () => {
    if (!selectedCliente) { toast.error("Selecione um cliente"); return; }
    if (selectedProdutos.length === 0 && selectedAcessorios.length === 0) { 
      toast.error("Adicione ao menos um produto ou acessório"); 
      return; 
    }
    if (!formaPagamento) { toast.error("Selecione a forma de pagamento"); return; }
    if (possuiTroca) {
      const valorRecebido = parseFloat(trocaValorRecebido);
      if (!trocaModeloId) { toast.error("Selecione o modelo do aparelho recebido"); return; }
      if (!trocaCor.trim() || !trocaMemoria.trim()) { toast.error("Cor e memória da troca são obrigatórios"); return; }
      if (isNaN(valorRecebido) || valorRecebido <= 0) { toast.error("Informe um valor recebido válido para a troca"); return; }
    }

    setSubmitting(true);
    try {
      // Garantia só se aplica quando há celulares
      const temCelulares = selectedProdutos.length > 0;
      const garantiaFinal = temCelulares ? (parseInt(garantiaMeses) || 0) : 0;
      
      await axios.post(`${API}/loja/${lojaSlug}/vendas`, {
        cliente_id: selectedCliente.id,
        produtos: selectedProdutos.map(p => p.id),
        acessorios: selectedAcessorios.map(a => ({ id: a.id, quantidade: a.quantidade })),
        forma_pagamento: formaPagamento,
        observacao: observacao || null,
        garantia_meses: garantiaFinal,
        garantia_inicio: garantiaFinal > 0 && garantiaInicio ? garantiaInicio : null,
        desconto: descontoValue > 0 ? descontoValue : null,
        possui_troca: possuiTroca,
        troca: possuiTroca ? {
          modelo_id: trocaModeloId,
          cor: trocaCor.trim(),
          memoria: trocaMemoria.trim(),
          memoria_ram: trocaMemoriaRam.trim() || null,
          bateria: trocaBateria ? parseInt(trocaBateria) : null,
          imei: trocaImei.trim() || null,
          valor_recebido: parseFloat(trocaValorRecebido)
        } : null
      });
      toast.success("Venda finalizada com sucesso!");
      navigate(`/${lojaSlug}/vendas`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao finalizar venda");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-[#D4AF37]">Carregando...</div></div>;

  return (
    <div className="space-y-6" data-testid="ponto-venda-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-[#D4AF37]" /></div>
        <div><h1 className="text-2xl font-bold text-white font-['Outfit']">Ponto de Venda</h1><p className="text-sm text-gray-400">Realize vendas para seus clientes</p></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#141414] border border-white/5">
              <TabsTrigger value="celulares" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-celulares">
                <Package className="w-4 h-4 mr-2" />Celulares
              </TabsTrigger>
              <TabsTrigger value="acessorios" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-acessorios">
                <Headphones className="w-4 h-4 mr-2" />Acessórios
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="celulares" className="mt-4">
              <Card className="bg-[#141414] border border-white/5">
                <CardHeader className="border-b border-white/5 py-4"><div className="flex items-center gap-3"><Package className="w-5 h-5 text-blue-500" /><CardTitle className="text-base font-semibold text-white">Celulares Disponíveis</CardTitle></div></CardHeader>
                <CardContent className="p-4">
                  <div className="relative mb-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" /><Input placeholder="Buscar celular..." value={searchProduto} onChange={(e) => setSearchProduto(e.target.value)} className="pl-10 bg-[#0A0A0A] border-white/10 text-white" data-testid="search-produto-venda" /></div>
                  <div className="max-h-[400px] overflow-y-auto space-y-2">
                    {filteredProdutos.length > 0 ? filteredProdutos.map((produto) => (
                      <div key={produto.id} className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg border border-white/5 hover:border-[#D4AF37]/30 transition-colors">
                        <div className="flex-1"><p className="text-[#D4AF37] font-medium">{produto.modelo_nome}</p><p className="text-sm text-gray-400">{produto.cor} • {produto.memoria}{produto.bateria && ` • ${produto.bateria}%`}</p></div>
                        <div className="flex items-center gap-3"><span className="text-green-500 font-semibold">{formatCurrency(produto.preco)}</span><Button size="sm" onClick={() => setSelectedProdutos([...selectedProdutos, produto])} className="bg-[#D4AF37] text-black hover:bg-[#B5952F]" data-testid={`add-produto-${produto.id}`}><Plus className="w-4 h-4" /></Button></div>
                      </div>
                    )) : <p className="text-center text-gray-500 py-8">Nenhum celular disponível</p>}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="acessorios" className="mt-4">
              <Card className="bg-[#141414] border border-white/5">
                <CardHeader className="border-b border-white/5 py-4"><div className="flex items-center gap-3"><Headphones className="w-5 h-5 text-purple-500" /><CardTitle className="text-base font-semibold text-white">Acessórios Disponíveis</CardTitle></div></CardHeader>
                <CardContent className="p-4">
                  <div className="relative mb-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" /><Input placeholder="Buscar acessório..." value={searchAcessorio} onChange={(e) => setSearchAcessorio(e.target.value)} className="pl-10 bg-[#0A0A0A] border-white/10 text-white" data-testid="search-acessorio-venda" /></div>
                  <div className="max-h-[400px] overflow-y-auto space-y-2">
                    {filteredAcessorios.length > 0 ? filteredAcessorios.map((acessorio) => (
                      <div key={acessorio.id} className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg border border-white/5 hover:border-purple-500/30 transition-colors">
                        <div className="flex-1">
                          <p className="text-purple-400 font-medium">{acessorio.nome}</p>
                          <p className="text-sm text-gray-400">{acessorio.categoria} • Estoque: {acessorio.quantidade}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-green-500 font-semibold">{formatCurrency(acessorio.preco)}</span>
                          <Button size="sm" onClick={() => addAcessorio(acessorio)} className="bg-purple-600 text-white hover:bg-purple-700" data-testid={`add-acessorio-${acessorio.id}`}><Plus className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    )) : <p className="text-center text-gray-500 py-8">Nenhum acessório disponível</p>}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-4">
          <Card className="bg-[#141414] border border-white/5">
            <CardHeader className="border-b border-white/5 py-4"><div className="flex items-center gap-3"><User className="w-5 h-5 text-green-500" /><CardTitle className="text-base font-semibold text-white">Cliente</CardTitle></div></CardHeader>
            <CardContent className="p-4">
              {selectedCliente ? (
                <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                  <div><p className="text-white font-medium">{selectedCliente.nome}</p><p className="text-sm text-gray-400">{selectedCliente.whatsapp}</p></div>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedCliente(null)} className="text-gray-400 hover:text-red-400" data-testid="remove-cliente"><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <>
                  <div className="relative mb-3"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" /><Input placeholder="Buscar cliente..." value={searchCliente} onChange={(e) => setSearchCliente(e.target.value)} className="pl-10 bg-[#0A0A0A] border-white/10 text-white" data-testid="search-cliente-venda" /></div>
                  {searchCliente && <div className="max-h-[200px] overflow-y-auto space-y-2">{filteredClientes.map((c) => (<div key={c.id} onClick={() => { setSelectedCliente(c); setSearchCliente(""); }} className="p-3 bg-[#1A1A1A] rounded-lg border border-white/5 hover:border-[#D4AF37]/30 cursor-pointer" data-testid={`select-cliente-${c.id}`}><p className="text-white font-medium">{c.nome}</p><p className="text-sm text-gray-400">{c.cpf}</p></div>))}</div>}
                </>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#141414] border border-white/5">
            <CardHeader className="border-b border-white/5 py-4"><div className="flex items-center justify-between"><div className="flex items-center gap-3"><ShoppingCart className="w-5 h-5 text-[#D4AF37]" /><CardTitle className="text-base font-semibold text-white">Carrinho</CardTitle></div><span className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-semibold rounded">{selectedProdutos.length + selectedAcessorios.length} itens</span></div></CardHeader>
            <CardContent className="p-4">
              {selectedProdutos.length > 0 || selectedAcessorios.length > 0 ? (
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {/* Celulares no carrinho */}
                  {selectedProdutos.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Celulares</p>
                      {selectedProdutos.map((p) => (
                        <div key={p.id} className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg border border-white/5">
                          <div className="flex-1 min-w-0"><p className="text-white font-medium truncate">{p.modelo_nome}</p><p className="text-sm text-gray-400 truncate">{p.cor} • {p.memoria}</p></div>
                          <div className="flex items-center gap-2"><span className="text-green-500 font-semibold text-sm">{formatCurrency(p.preco)}</span><Button size="sm" variant="ghost" onClick={() => setSelectedProdutos(selectedProdutos.filter(sp => sp.id !== p.id))} className="text-gray-400 hover:text-red-400 p-1" data-testid={`remove-produto-${p.id}`}><X className="w-4 h-4" /></Button></div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Acessórios no carrinho */}
                  {selectedAcessorios.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Acessórios</p>
                      {selectedAcessorios.map((a) => (
                        <div key={a.id} className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg border border-purple-500/20">
                          <div className="flex-1 min-w-0"><p className="text-purple-400 font-medium truncate">{a.nome}</p><p className="text-sm text-gray-400">Qtd: {a.quantidade} × {formatCurrency(a.preco)}</p></div>
                          <div className="flex items-center gap-2">
                            <span className="text-green-500 font-semibold text-sm">{formatCurrency(a.preco * a.quantidade)}</span>
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="ghost" onClick={() => updateAcessorioQtd(a.id, -1)} className="text-gray-400 hover:text-white p-1 h-6 w-6" data-testid={`dec-acessorio-${a.id}`}><Minus className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => updateAcessorioQtd(a.id, 1)} className="text-gray-400 hover:text-white p-1 h-6 w-6" data-testid={`inc-acessorio-${a.id}`}><Plus className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => removeAcessorio(a.id)} className="text-gray-400 hover:text-red-400 p-1 h-6 w-6" data-testid={`remove-acessorio-${a.id}`}><X className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : <p className="text-center text-gray-500 py-6">Carrinho vazio</p>}
            </CardContent>
          </Card>

          <Card className="bg-[#141414] border border-white/5">
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2"><Label className="text-gray-300">Forma de Pagamento *</Label>
                <Select value={formaPagamento} onValueChange={setFormaPagamento}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="select-pagamento"><CreditCard className="w-4 h-4 mr-2 text-gray-500" /><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10">
                    <SelectItem value="dinheiro" className="text-gray-300">Dinheiro</SelectItem>
                    <SelectItem value="pix" className="text-gray-300">PIX</SelectItem>
                    <SelectItem value="cartao_credito" className="text-gray-300">Cartão de Crédito</SelectItem>
                    <SelectItem value="cartao_debito" className="text-gray-300">Cartão de Débito</SelectItem>
                    <SelectItem value="transferencia" className="text-gray-300">Transferência</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Campo de Garantia - Apenas para celulares */}
              {selectedProdutos.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-400" />
                    Garantia (Celulares)
                  </Label>
                  <Select value={garantiaMeses} onValueChange={setGarantiaMeses}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="select-garantia">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-white/10">
                      <SelectItem value="0" className="text-gray-300">Sem garantia</SelectItem>
                      <SelectItem value="1" className="text-gray-300">1 mês</SelectItem>
                      <SelectItem value="3" className="text-gray-300">3 meses</SelectItem>
                      <SelectItem value="6" className="text-gray-300">6 meses</SelectItem>
                      <SelectItem value="12" className="text-gray-300">12 meses</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedProdutos.length > 0 && parseInt(garantiaMeses) > 0 && (
                <div className="space-y-2">
                  <Label className="text-gray-300">Data de início da garantia</Label>
                  <Input
                    type="date"
                    value={garantiaInicio}
                    onChange={(e) => setGarantiaInicio(e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white"
                    data-testid="input-garantia-inicio"
                  />
                </div>
              )}
              
              {/* Campo de Desconto */}
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Percent className="w-4 h-4 text-green-400" />
                  Desconto (R$)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0,00"
                  value={desconto}
                  onChange={(e) => setDesconto(e.target.value)}
                  disabled={possuiTroca}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="input-desconto"
                />
                {possuiTroca && <p className="text-xs text-yellow-400">Desconto automático pela troca: {formatCurrency(descontoValue)}</p>}
              </div>

              <div className="space-y-3 rounded-lg border border-white/10 p-3 bg-[#101010]">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300">Possui troca?</Label>
                  <Switch checked={possuiTroca} onCheckedChange={setPossuiTroca} data-testid="switch-possui-troca" />
                </div>
                {possuiTroca && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-gray-300">Modelo do aparelho recebido *</Label>
                      <Select value={trocaModeloId} onValueChange={setTrocaModeloId}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="select-troca-modelo">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#141414] border-white/10">
                          {modelos.map((m) => (
                            <SelectItem key={m.id} value={m.id} className="text-gray-300">{m.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label className="text-gray-300">Cor *</Label><Input value={trocaCor} onChange={(e) => setTrocaCor(e.target.value)} placeholder="Ex: Preto" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="input-troca-cor" /></div>
                    <div className="space-y-2"><Label className="text-gray-300">Memória *</Label><Input value={trocaMemoria} onChange={(e) => setTrocaMemoria(e.target.value)} placeholder="Ex: 128GB" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="input-troca-memoria" /></div>
                    <div className="space-y-2"><Label className="text-gray-300">Memória RAM</Label><Input value={trocaMemoriaRam} onChange={(e) => setTrocaMemoriaRam(e.target.value)} placeholder="Ex: 8GB (opcional)" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="input-troca-memoria-ram" /></div>
                    <div className="space-y-2"><Label className="text-gray-300">Bateria (%)</Label><Input type="number" value={trocaBateria} onChange={(e) => setTrocaBateria(e.target.value)} placeholder="Ex: 85" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="input-troca-bateria" /></div>
                    <div className="space-y-2"><Label className="text-gray-300">IMEI</Label><Input value={trocaImei} onChange={(e) => setTrocaImei(e.target.value)} placeholder="Ex: 123456789" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="input-troca-imei" /></div>
                    <div className="space-y-2 md:col-span-2"><Label className="text-gray-300">Valor recebido na troca (R$) *</Label><Input type="number" min="0" step="0.01" value={trocaValorRecebido} onChange={(e) => setTrocaValorRecebido(e.target.value)} placeholder="Ex: 1500" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="input-troca-valor" /></div>
                    <p className="text-xs text-gray-400 md:col-span-2">O celular recebido será cadastrado automaticamente no estoque e o valor informado será aplicado como desconto da venda.</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-2"><Label className="text-gray-300">Observação</Label><Textarea placeholder="Observações..." value={observacao} onChange={(e) => setObservacao(e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white min-h-[60px]" data-testid="input-observacao" /></div>
              
              <div className="pt-4 border-t border-white/10">
                {descontoValue > 0 && (
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-sm">Subtotal</span>
                      <span className="text-gray-400">{formatCurrency(total)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-green-500 text-sm">Desconto</span>
                      <span className="text-green-500">-{formatCurrency(descontoValue)}</span>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-400 uppercase text-sm tracking-wider">Total</span>
                  <span className="text-2xl font-bold text-[#D4AF37]" data-testid="total-value">{formatCurrency(totalComDesconto)}</span>
                </div>
                <Button onClick={handleSubmit} disabled={submitting || !selectedCliente || (selectedProdutos.length === 0 && selectedAcessorios.length === 0) || !formaPagamento} className="w-full h-12 bg-[#D4AF37] text-black font-bold hover:bg-[#B5952F] disabled:opacity-50" data-testid="btn-finalizar-venda">
                  {submitting ? "Finalizando..." : <><CheckCircle className="w-5 h-5 mr-2" />Finalizar Venda</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PontoVenda;
